/**
 * useAvatarState — manages avatar state and overlay visibility.
 *
 * Observes TTS playback, streaming, and banker notification signals
 * to derive the correct avatar expression state.
 *
 * Story v3-7-4: 2D Animated Banker Character
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { AvatarState } from '../components/avatar/BankerAvatar';

interface BankerNotification {
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface UseAvatarStateParams {
  /** Whether TTS audio queue is currently playing (from useTTS) */
  isQueuePlaying: boolean;
  /** Whether a single TTS track is playing (from useTTS) */
  isPlaying: boolean;
  /** Whether the AI stream is active (response being typed) */
  isStreaming: boolean;
  /** Whether voice recording is active */
  isRecording?: boolean;
  /** Most recent banker notification (null if none) */
  bankerNotification?: BankerNotification | null;
}

interface UseAvatarStateReturn {
  avatarState: AvatarState;
  isVisible: boolean;
  setIsVisible: (v: boolean) => void;
  isMinimized: boolean;
  setIsMinimized: (v: boolean) => void;
}

export function useAvatarState({
  isQueuePlaying,
  isPlaying,
  isStreaming,
  isRecording = false,
  bankerNotification = null,
}: UseAvatarStateParams): UseAvatarStateReturn {
  const [avatarState, setAvatarState] = useState<AvatarState>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const autoMinimizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSpeaking = useRef(false);

  // ── Derive avatar state ────────────────────────────────────────
  useEffect(() => {
    if (isQueuePlaying || isPlaying) {
      setAvatarState('speaking');
    } else if (isStreaming) {
      setAvatarState('thinking');
    } else if (isRecording) {
      setAvatarState('idle');
    } else {
      // Keep the last non-idle state for a moment before reverting
      // (this is handled by the auto-minimize logic below)
      setAvatarState((prev) => {
        if (prev === 'speaking' || prev === 'thinking') return 'idle';
        return prev;
      });
    }
  }, [isQueuePlaying, isPlaying, isStreaming, isRecording]);

  // ── Auto-show on speaking; auto-minimize after speaking ends ───
  useEffect(() => {
    const isSpeaking = isQueuePlaying || isPlaying;

    if (isSpeaking && !prevSpeaking.current) {
      // Started speaking — show the overlay
      setIsVisible(true);
      setIsMinimized(false);
      // Cancel any pending auto-minimize
      if (autoMinimizeTimerRef.current) {
        clearTimeout(autoMinimizeTimerRef.current);
        autoMinimizeTimerRef.current = null;
      }
    } else if (!isSpeaking && prevSpeaking.current) {
      // Stopped speaking — schedule auto-minimize after 3 s
      autoMinimizeTimerRef.current = setTimeout(() => {
        setIsMinimized(true);
      }, 3000);
    }

    prevSpeaking.current = isSpeaking;
  }, [isQueuePlaying, isPlaying]);

  // ── Also show avatar when the assistant starts streaming (covers no-voice mode) ──
  const prevStreaming = useRef(false);
  useEffect(() => {
    if (isStreaming && !prevStreaming.current) {
      // Started streaming — show overlay in "thinking" state
      setIsVisible(true);
      setIsMinimized(false);
      if (autoMinimizeTimerRef.current) {
        clearTimeout(autoMinimizeTimerRef.current);
        autoMinimizeTimerRef.current = null;
      }
    } else if (!isStreaming && prevStreaming.current) {
      // Streaming finished and TTS not active — auto-minimize after 5 s
      const isSpeakingNow = isQueuePlaying || isPlaying;
      if (!isSpeakingNow) {
        autoMinimizeTimerRef.current = setTimeout(() => {
          setIsMinimized(true);
        }, 5000);
      }
    }
    prevStreaming.current = isStreaming;
  }, [isStreaming, isQueuePlaying, isPlaying]);

  // ── React to banker notifications ──────────────────────────────
  const prevNotif = useRef<BankerNotification | null>(null);

  useEffect(() => {
    if (!bankerNotification) return;
    if (bankerNotification === prevNotif.current) return;

    prevNotif.current = bankerNotification;

    // Cancel any pending auto-minimize so the notification isn't dismissed
    if (autoMinimizeTimerRef.current) {
      clearTimeout(autoMinimizeTimerRef.current);
      autoMinimizeTimerRef.current = null;
    }

    // Show overlay on new notification
    setIsVisible(true);
    setIsMinimized(false);

    // Map priority to avatar expression
    if (bankerNotification.priority === 'HIGH') {
      setAvatarState('concerned');
    } else if (bankerNotification.priority === 'MEDIUM') {
      setAvatarState('confident');
    } else {
      setAvatarState('greeting');
    }
  }, [bankerNotification]);

  // ── Cleanup on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (autoMinimizeTimerRef.current) {
        clearTimeout(autoMinimizeTimerRef.current);
      }
    };
  }, []);

  const handleSetIsVisible = useCallback((v: boolean) => {
    setIsVisible(v);
    if (!v) {
      // Reset to idle when hidden
      setAvatarState('idle');
    }
  }, []);

  return {
    avatarState,
    isVisible,
    setIsVisible: handleSetIsVisible,
    isMinimized,
    setIsMinimized,
  };
}
